/*
 * Loyalty account web components — read window.loyaltylion (.customer + .program)
 * and render the loyalty UI. No API calls; consume the variables the LoyaltyLion
 * app injects. LoyaltyLion is the single source of truth for tiers + benefits
 * (no Shopify metaobject / theme-setting tier config anymore).
 *
 *   <loyalty-tiers>    points progress + tier progress recap  (snippets/loyalty-recap.liquid)
 *   <loyalty-benefits> tier benefits comparison table         (snippets/loyalty-tiers-table.liquid)
 *
 * LoyaltyLion units: points are whole points; spend/tier bounds are in cents.
 *
 * Dev toggle: append ?loyalty_mock=1 to inject a sample dataset before LoyaltyLion is live.
 */
(function () {
  var MOCK = new URLSearchParams(window.location.search).get('loyalty_mock') === '1';

  /* ---------- shared helpers ---------- */
  function num(v) {
    var n = parseFloat(String(v == null ? '' : v).replace(/[^0-9.\-]/g, ''));
    return Number.isFinite(n) ? n : 0;
  }
  function fmt(n) { return num(n).toLocaleString('en-US'); }
  // cents -> "$1,234" (rounded up to the next whole dollar)
  function moneyCents(c) { return '$' + Math.ceil(num(c) / 100).toLocaleString('en-US'); }
  // cents -> "$100" (exact whole dollars, for tier threshold labels)
  function dollarsCents(c) { return '$' + Math.round(num(c) / 100).toLocaleString('en-US'); }
  // "2026-10-21" -> "October 21, 2026". Parse the parts manually so the date is
  // not shifted a day by UTC/local timezone conversion. Returns '' if unparseable.
  function fmtDate(s) {
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
    if (!m) return '';
    var d = new Date(+m[1], +m[2] - 1, +m[3]);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  // Normalize LoyaltyLion tiers -> [{ id, name, minSpend(cents), upper(cents|null), position }]
  // sorted by position, hidden tiers removed.
  function normalizeTiers(raw) {
    if (!Array.isArray(raw)) return [];
    return raw
      .filter(function (t) { return t && !t.hidden; })
      .map(function (t) {
        var bounds = t.bounds || {};
        return {
          id: t.id,
          name: t.name || t.title || '',
          minSpend: num(bounds.lower != null ? bounds.lower : (t.minSpend != null ? t.minSpend : t.threshold)),
          upper: bounds.upper == null ? null : num(bounds.upper),
          position: num(t.position),
        };
      })
      .sort(function (a, b) { return a.position - b.position || a.minSpend - b.minSpend; });
  }

  function tierRange(tier) {
    if (tier.upper == null) return `Spend ${dollarsCents(tier.minSpend)}+`;
    if (tier.minSpend === 0) return 'Free to join';
    return `Spend ${dollarsCents(tier.minSpend)}–${dollarsCents(tier.upper)}`;
  }

  // LoyaltyLion is injected asynchronously by the app. Poll briefly for it.
  // `ready(ll)` returns true once the data this component needs is present.
  function waitForData(ready, mockData) {
    if (MOCK) return Promise.resolve(mockData());
    return new Promise(function (resolve, reject) {
      var started = Date.now();
      (function check() {
        var ll = window.loyaltylion || window.LoyaltyLion;
        if (ll && ready(ll)) return resolve(ll);
        if (Date.now() - started > 10000) return reject(new Error('LoyaltyLion not ready'));
        requestAnimationFrame(check);
      })();
    });
  }

  function mockData() {
    return {
      customer: {
        pointsApproved: 1250,
        pointsPending: 0,
        loyaltyTierMembership: { loyaltyTierId: 29092 },
        loyaltyTierEligibleSpend: 37188,
        loyaltyTierEligibleSpendAtMembershipExpiration: 0,
        loyaltyTierEligibilityValidUntil: '2027-05-15T22:02:14.000Z',
      },
      program: {
        name: "'47 Family",
        loyaltyTiers: [
          { id: 29090, name: 'Member', position: 0, bounds: { lower: 0, upper: 10000 } },
          { id: 29091, name: 'Loyalist', position: 1, bounds: { lower: 10000, upper: 30000 } },
          { id: 29092, name: 'Legacy', position: 2, bounds: { lower: 30000, upper: null } },
        ],
        loyaltyTierBenefits: [
          { id: 1, content: 'Free Shipping', loyaltyTierIds: [29090, 29091, 29092], secret: false },
          { id: 2, content: 'Exclusive Member Offers', loyaltyTierIds: [29091, 29092], secret: false },
          { id: 3, content: 'Invitations to ’47 Events', loyaltyTierIds: [29092], secret: false },
        ],
        rewards: [
          { title: '$5 discount', point_cost: 500 },
          { title: '$10 discount', point_cost: 1000 },
          { title: '$15 discount', point_cost: 1500 },
        ],
      },
    };
  }

  /* ================= <loyalty-tiers> — points + tier recap ================= */
  if (!customElements.get('loyalty-tiers')) {
    customElements.define('loyalty-tiers', class extends HTMLElement {
      connectedCallback() {
        waitForData(function (ll) { return !!ll.customer; }, mockData)
          .then(this.render.bind(this))
          .catch(function () {}.bind(this));

        // Smooth-scroll for the "See Your Tier Benefits" anchor CTA.
        this.addEventListener('click', function (e) {
          var trigger = e.target.closest('[data-scroll-to]');
          if (!trigger) return;
          var target = document.querySelector(trigger.getAttribute('data-scroll-to'));
          if (target) { e.preventDefault(); target.scrollIntoView({ behavior: 'smooth' }); }
        });
      }

      render(ll) {
        var customer = ll && ll.customer ? ll.customer : null;
        var program = ll && ll.program ? ll.program : {};
        this.renderRewardProgress(customer, program);
        this.renderTierProgress(customer, program);
      }

      /* ---------- Points → next reward ---------- */
      renderRewardProgress(customer, program) {
        var points = this.getPoints(customer);
        var rewards = Array.isArray(program.rewards) ? program.rewards : [];
        var costs = rewards
          .map(function (r) { return num(r.point_cost != null ? r.point_cost : (r.pointsCost != null ? r.pointsCost : r.cost)); })
          .filter(function (c) { return c > 0; })
          .sort(function (a, b) { return a - b; });

        // Cheapest reward the customer can't yet afford.
        var nextCost = costs.find(function (c) { return c > points; });
        if (nextCost == null) nextCost = costs.length ? costs[costs.length - 1] : 0;
        var remaining = Math.max(0, nextCost - points);
        var pct = nextCost > 0 ? Math.min(100, Math.round((points / nextCost) * 100)) : 0;

        this.setText('[data-recap-reward-remaining]', fmt(remaining));
        this.setText('[data-recap-reward-endpoint-max]', fmt(nextCost));
        this.setWidth('[data-recap-reward-fill]', pct);
        this.setLeft('[data-recap-reward-knob]', pct);
      }

      /* ---------- Spend → next tier ---------- */
      renderTierProgress(customer, program) {
        var tiers = normalizeTiers(program.loyaltyTiers);

        // No tier program yet → data-tiers-empty hides the tier column via CSS
        // (group-data-[tiers-empty]:hidden), leaving the points column on its own.
        if (!tiers.length || !customer) {
          this.setAttribute('data-tiers-empty', '');
          return;
        }
        this.removeAttribute('data-tiers-empty');

        // Scale + name labels under the progress bar, straight from the tier list.
        this.renderTierLabels(tiers);

        var spend = num(customer.loyaltyTierEligibleSpend);
        // Current tier = the membership LoyaltyLion assigns; fall back to the highest
        // threshold the customer's eligible spend has met. Prefer membership so a
        // member whose eligible spend resets for a new period still reads as their
        // locked-in tier (and gets a retain-status prompt, not a fresh upgrade one).
        var currentIndex = 0;
        tiers.forEach(function (t, i) { if (spend >= t.minSpend) currentIndex = i; });
        var membershipId = customer.loyaltyTierMembership && customer.loyaltyTierMembership.loyaltyTierId;
        var membershipIndex = tiers.findIndex(function (t) { return t.id === membershipId; });
        if (membershipIndex !== -1) currentIndex = membershipIndex;
        var currentName = tiers[currentIndex].name;
        var next = tiers[currentIndex + 1];

        this.setText('[data-recap-tier-name]', currentName);

        // "Spend $X by {date} to upgrade your status" — loyaltyTierEligibilityValidUntil
        // is the tier lock-in / membership expiry date and doubles as the deadline shown
        // in LoyaltyLion's own Tier Progress copy. Drop the "by {date}" clause if the
        // field is missing/unparseable.
        var msgEl = this.querySelector('[data-recap-tier-message]');
        if (msgEl) {
          var by = fmtDate(customer.loyaltyTierEligibilityValidUntil);
          if (next) {
            // Upgrade: gap from current rolling-window spend to the next threshold.
            var gap = Math.max(0, next.minSpend - spend);
            msgEl.textContent = 'Spend ' + moneyCents(gap) + (by ? ' by ' + by : '') + ' to upgrade your status';
            msgEl.hidden = false;
          } else {
            // Top tier: no upgrade left, so prompt to retain the current tier. Tiers use a
            // rolling spend window; loyaltyTierEligibleSpendAtMembershipExpiration is the
            // spend still counting once current purchases age out, so the retention gap is
            // the threshold minus that projected figure. Gated on loyaltyTierEligibilityValidUntil:
            // no valid-until date = no re-eval deadline to show, so hide the prompt.
            var projected = num(customer.loyaltyTierEligibleSpendAtMembershipExpiration);
            var keepGap = Math.max(0, tiers[currentIndex].minSpend - projected);
            if (by && keepGap > 0) {
              msgEl.textContent = 'Spend ' + moneyCents(keepGap) + ' by ' + by + ' to keep your status';
              msgEl.hidden = false;
            } else {
              msgEl.hidden = true;
            }
          }
        }

        // Knob position: equal-width segments per tier, interpolated within the current segment.
        var n = tiers.length;
        var frac;
        if (next) {
          var cur = tiers[currentIndex];
          var span = next.minSpend - cur.minSpend;
          frac = span > 0 ? Math.min(1, (spend - cur.minSpend) / span) : 0;
        } else {
          frac = 1;
        }
        var pct = Math.min(100, ((currentIndex + frac) / (n - 1 || 1)) * 100);
        this.setWidth('[data-recap-tier-fill]', pct);
        this.setLeft('[data-recap-tier-knob]', pct);
      }

      // Build the two label rows around the tier bar from the tier list.
      renderTierLabels(tiers) {
        var scale = this.querySelector('[data-recap-scale]');
        var labels = this.querySelector('[data-recap-tier-labels]');
        var last = tiers.length - 1;
        if (scale) {
          scale.textContent = '';
          tiers.forEach(function (t, i) {
            var s = document.createElement('span');
            s.textContent = dollarsCents(t.minSpend) + (i === last ? '+' : '');
            scale.appendChild(s);
          });
        }
        if (labels) {
          labels.textContent = '';
          tiers.forEach(function (t) {
            var s = document.createElement('span');
            s.textContent = t.name;
            labels.appendChild(s);
          });
        }
      }

      /* ---------- helpers ---------- */
      getPoints(customer) {
        if (customer) {
          var v = customer.pointsApproved != null ? customer.pointsApproved
            : (customer.pointsAmount != null ? customer.pointsAmount : customer.points);
          if (v != null) return num(v);
        }
        // Fall back to the SDK-populated points span if the object key differs.
        var span = this.querySelector('[data-lion-points]:not([data-lion-points="pending"])');
        return span ? num(span.textContent) : 0;
      }
      setText(sel, text) { var el = this.querySelector(sel); if (el) el.textContent = text; }
      setWidth(sel, pct) { var el = this.querySelector(sel); if (el) el.style.width = pct + '%'; }
      // Position the knob and clamp it inside the track: X offset scales from 0
      // (at 0%) to -100% (at 100%) so the logo never overhangs either end; Y stays
      // centered. Set the CSS `translate` property (what Tailwind's -translate-*
      // utilities use) so it overrides them instead of stacking via `transform`.
      setLeft(sel, pct) {
        var el = this.querySelector(sel);
        if (el) { el.style.left = pct + '%'; el.style.translate = (-pct) + '% -50%'; }
      }
    });
  }

  /* ================= <loyalty-benefits> — tier benefits table ================= */
  if (!customElements.get('loyalty-benefits')) {
    // Per-tier column styling by position (full literal classes so Tailwind's
    // source scan picks them up). Each tier column gets a solid background;
    // the dark Legacy column flips to light text/dots for contrast.
    var COL = [
      { bg: 'bg-[#d7dbd2]', head: 'text-primary-dark', sub: 'text-primary-dark', dot: 'bg-primary-dark', div: 'border-black/10' },
      { bg: 'bg-[#8f9d8e]', head: 'text-primary-dark', sub: 'text-primary-dark', dot: 'bg-primary-dark', div: 'border-black/10' },
      { bg: 'bg-forest', head: 'text-primary-light', sub: 'text-primary-light', dot: 'bg-primary-light', div: 'border-white/20' },
    ];
    function col(i) { return COL[i] || COL[COL.length - 1]; }

    customElements.define('loyalty-benefits', class extends HTMLElement {
      connectedCallback() {
        waitForData(function (ll) { return !!ll.program; }, mockData)
          .then(this.render.bind(this))
          .catch(function () {}.bind(this));
      }

      render(ll) {
        var program = ll && ll.program ? ll.program : {};
        var tiers = normalizeTiers(program.loyaltyTiers);
        var benefits = (Array.isArray(program.loyaltyTierBenefits) ? program.loyaltyTierBenefits : [])
          .filter(function (b) { return b && !b.secret; });

        var table = this.querySelector('[data-benefits-table]');
        if (!table || !tiers.length || !benefits.length) return; // stay hidden

        table.appendChild(this.buildHead(tiers));
        table.appendChild(this.buildBody(tiers, benefits));
        this.hidden = false;
      }

      buildHead(tiers) {
        var thead = document.createElement('thead');
        var tr = document.createElement('tr');

        var corner = document.createElement('th');
        corner.scope = 'col';
        corner.className = 'text-left align-middle';
        // Desktop header (mobile shows the standalone <h2> in the snippet instead).
        var cornerLabel = document.createElement('span');
        cornerLabel.className = 'hidden md:block font-heading font-normal text-3xl text-primary-dark';
        cornerLabel.textContent = 'Benefits';
        corner.appendChild(cornerLabel);
        tr.appendChild(corner);

        tiers.forEach(function (t, i) {
          var c = col(i);
          var th = document.createElement('th');
          th.scope = 'col';
          th.className = 'text-center align-bottom w-[18%] min-w-[90px] px-1 md:px-3 pt-4 md:pt-10 pb-2 md:pb-4 border-b ' +
            c.bg + ' ' + c.div;

          var name = document.createElement('span');
          name.className = 'block font-heading font-normal text-base md:text-3xl ' + c.head;
          name.textContent = t.name;

          var range = document.createElement('span');
          range.className = 'block text-xs md:text-base font-normal mt-0.5 min-h-[2rem] md:min-h-0 ' + c.sub;
          range.textContent = tierRange(t);

          th.appendChild(name);
          th.appendChild(range);
          tr.appendChild(th);
        });

        thead.appendChild(tr);
        return thead;
      }

      buildBody(tiers, benefits) {
        var tbody = document.createElement('tbody');
        benefits.forEach(function (b, idx) {
          var isLast = idx === benefits.length - 1;
          var ids = Array.isArray(b.loyaltyTierIds) ? b.loyaltyTierIds : [];

          var tr = document.createElement('tr');

          var th = document.createElement('th');
          th.scope = 'row';
          th.className = ('text-left font-body font-normal text-[10px] md:text-sm text-primary-dark pr-2 md:pr-4 py-2 md:py-3.5 align-middle ' +
            (isLast ? '' : 'border-b border-primary-dark/12')).trim();
          th.textContent = b.content || '';
          tr.appendChild(th);

          tiers.forEach(function (t, i) {
            var c = col(i);
            var td = document.createElement('td');
            td.className = ('text-center px-1 md:px-3 py-2 md:py-3.5 align-middle ' + c.bg + ' ' +
              (isLast ? '' : 'border-b ' + c.div)).trim();
            if (ids.indexOf(t.id) !== -1) {
              var dot = document.createElement('span');
              dot.className = 'inline-block w-2 h-2 rounded-full ' + c.dot;
              dot.setAttribute('role', 'img');
              dot.setAttribute('aria-label', 'Included');
              td.appendChild(dot);
            } else {
              var sr = document.createElement('span');
              sr.className = 'sr-only';
              sr.textContent = 'Not included';
              td.appendChild(sr);
            }
            tr.appendChild(td);
          });

          tbody.appendChild(tr);
        });
        return tbody;
      }
    });
  }
})();
